import type { ConciseWorkspace } from '@festina/configuration-general-types/configuration'
import type { WorkspaceKey } from '@festina-life/api'
import { getObjectEntries, notEmpty, type Optional } from '@festina-life/core'
import type { TreeNodeData } from '@festina-life/ui/listsAndTables'
import { useMemo } from 'react'

import { useAllConciseBranches } from '../../uiModels/branches/useAllConciseBranches'
import { useWorkspaceManagerContext } from '../../workspace/workspaceManagerContext'
import type { WorkspaceLeaf } from './types'
import { generateServerTree } from './workspacesTree/helpers'

export const useWorkspacesTree = ({
  query,
  activeWorkspaceKey,
}: {
  query: string
  activeWorkspaceKey: Optional<WorkspaceKey>
}): TreeNodeData<WorkspaceLeaf>[] => {
  const conciseBranches = useAllConciseBranches()
  const { conciseWorkspaces } = useWorkspaceManagerContext()
  const needle = query.toLocaleLowerCase()

  return useMemo(
    () =>
      getObjectEntries(conciseBranches)
        .map(([server, serverBranches]) => {
          const workspacesForServer =
            conciseWorkspaces[server]?.filter((workspace) =>
              workspace.name?.toLocaleLowerCase().includes(needle),
            ) ?? []

          const { orphanWorkspaces, nonOrphanWorkspaces } =
            workspacesForServer.reduce<{
              orphanWorkspaces: ConciseWorkspace[]
              nonOrphanWorkspaces: ConciseWorkspace[]
            }>(
              (acc, workspace) => {
                const doesBranchExist = serverBranches?.some(
                  (branch) => branch.name === workspace.baseBranchName,
                )

                ;(doesBranchExist
                  ? acc.nonOrphanWorkspaces
                  : acc.orphanWorkspaces
                ).push(workspace)

                return acc
              },
              { orphanWorkspaces: [], nonOrphanWorkspaces: [] },
            )

          const tree = generateServerTree({
            server,
            activeWorkspaceKey,
            orphanWorkspaces,
            nonOrphanWorkspaces,
          })

          return tree.nodes.length || tree.leaves.length ? tree : undefined
        })
        .filter(notEmpty),
    [activeWorkspaceKey, conciseBranches, conciseWorkspaces, needle],
  )
}
